import { Injectable } from '@angular/core';
import { AppService } from './app.service';
import { AppEnum } from '../appenum';
import { EncryptionService } from './encryption.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class LoggingService {
  level = this._appenum.EntityOfLogLevel.All;
  deviceInfo: any;
  constructor(
    public _appservice: AppService,
    public _appenum: AppEnum,
    public encryptionService: EncryptionService,
    private deviceService: DeviceDetectorService,
  ) { }

  async handleVerifyInspectionResponseLog(
    severity: 'info' | 'warning' | 'critical' | 'error',
    logRequest: any,
    StatusCode: any,
    Message: any,
    logMessage: any,
    componentName: any,
    apiName: any,
    response: any
  ) {
    const logLevel =
      severity === 'critical'
        ? this._appenum.EntityOfLogLevel.Critical
        : severity === 'warning'
          ? this._appenum.EntityOfLogLevel.Warning
          : severity === 'info'
            ? this._appenum.EntityOfLogLevel.Info
            : this._appenum.EntityOfLogLevel.Error;

    // Full response kept, alongside the same responseCode/responseMsg
    // shape handleResponseLog uses, so log consumers expecting that shape
    // still find it.
    logRequest.response = {
      responseCode: StatusCode,
      responseMsg: Message,
      fullResponse: response
    };

    await this.apilogs(
      logLevel,
      logMessage,
      componentName,
      logRequest,
      apiName
    );
  }

  async handleResponseLog(
    severity: 'info' | 'warning' | 'critical' | 'error',
    logRequest: any,
    StatusCode: any,
    Message: any,
    logMessage: any,
    componentName: any,
    // response?: any,
    apiName?: any
  ) {
    const logLevel =
      severity === 'critical'
        ? this._appenum.EntityOfLogLevel.Critical
        : severity === 'warning'
          ? this._appenum.EntityOfLogLevel.Warning
          : severity === 'info'
            ? this._appenum.EntityOfLogLevel.Info
            : this._appenum.EntityOfLogLevel.Error;
    logRequest.response = { responseCode: StatusCode, responseMsg: Message };
    console.log("apiName", apiName)
    await this.apilogs(
      logLevel,
      logMessage,
      componentName,
      logRequest,
      apiName
    );
  }

  private byteSize(obj: any): number {
    return new TextEncoder().encode(JSON.stringify(obj)).length;
  }

  async handleLocalStorageLog(
    severity: 'info' | 'warning' | 'critical' | 'error',
    logRequest: any,
    StatusCode: any,
    Message: any,
    logMessage: any,
    componentName: any,
    apiName?: any
  ) {
    const MAX_BYTES = 210 * 1024;

    const logLevel =
      severity === 'critical'
        ? this._appenum.EntityOfLogLevel.Critical
        : severity === 'warning'
          ? this._appenum.EntityOfLogLevel.Warning
          : severity === 'info'
            ? this._appenum.EntityOfLogLevel.Info
            : this._appenum.EntityOfLogLevel.Error;

    logRequest.response = {
      responseCode: StatusCode,
      responseMsg: `${Message} | ${logRequest?.reqBody?.count ?? 0} keys`,
    };

    try {
      let sizeBytes = this.byteSize(logRequest);

      // too big: drop keys from the end until it fits
      const entries = logRequest?.reqBody?.entries;
      if (sizeBytes > MAX_BYTES && entries) {
        const keys = Object.keys(entries);
        const droppedKeys: string[] = [];
        logRequest.reqBody.truncated = true;
        logRequest.reqBody.droppedKeys = droppedKeys; // names only, counted in the size below

        while (keys.length && sizeBytes > MAX_BYTES) {
          const lastKey = keys.pop() as string;
          delete entries[lastKey];
          droppedKeys.push(lastKey);
          sizeBytes = this.byteSize(logRequest);
        }
        logRequest.reqBody.loggedCount = keys.length;
      }

      logRequest.sizeKB = Number((this.byteSize(logRequest) / 1024).toFixed(2));
      console.log(`logRequest size: ${logRequest.sizeKB} KB`, logRequest.reqBody?.truncated ? '(truncated)' : '');
    } catch (e) {
      console.error('could not measure or shrink logRequest', e);
    }

    console.log("logRequest", logRequest)
    try {
      await this.apilogs(logLevel, logMessage, componentName, logRequest, apiName);
    } catch (err) {
      console.error('handleLocalStorageLog apilogs failed', err);
    }
  }
  async logApiError(
    severity: 'info' | 'warning' | 'critical' | 'error',
    logRequest: any,
    error: any,
    logMessage: any,
    componentName: any,
    apiName?: string,
  ) {
    const logLevel =
      severity === 'critical'
        ? this._appenum.EntityOfLogLevel.Critical
        : severity === 'warning'
          ? this._appenum.EntityOfLogLevel.Warning
          : severity === 'info'
            ? this._appenum.EntityOfLogLevel.Info
            : this._appenum.EntityOfLogLevel.Error;
    logRequest.error = { result: error };
    // covers 'error' and default/nothing

    await this.apilogs(
      logLevel,
      logMessage,
      componentName,
      logRequest,
      apiName
    );
  }

  async logFunctionError(
    error: any,
    functionName: string,
    componentName: any,
    context?: any
  ): Promise<void> {

    const serializedError = error instanceof Error
      ? {
        message: error.message,
        name: error.name,
        stack: error.stack,
      }
      : error;
    const logPayload = {
      function: functionName,
      error: { result: serializedError },
      ...(context !== undefined && { context })
    };

    await this.apilogs(
      this._appenum.EntityOfLogLevel.Error,
      `Functional Error in ${functionName}()`,
      componentName,
      logPayload,
      undefined,
      functionName
    );
  }


  async logAppLifecycleEvent(
    eventType: 'page_refresh' | 'tab_hidden' | 'tab_visible',
    componentName: string,
    extra?: Record<string, any>
  ): Promise<void> {

    const messages: Record<string, string> = {
      page_refresh: 'Page was refreshed/reloaded',
      tab_hidden: 'Tab/app minimized or switched away',
      tab_visible: 'Tab/app returned to foreground'
    };

    const logPayload = {
      eventType,
      url: window.location.href,
      ...extra
    };

    await this.apilogs(
      this._appenum.EntityOfLogLevel.Info,
      messages[eventType],
      componentName,
      logPayload,
      undefined,
      'trackAppLifecycleEvents'
    );
  }

  async logInAppBrowserDetection(
    detectionResult: { platform: string; mailApp: string; url: string; referrer: string | null },
    componentName: string,
    action?: 'detected' | 'opened_in_browser' | 'copy_link' | 'copy_link_failed' | 'continued_anyway'
  ): Promise<void> {
    const resolvedAction = action ?? 'detected';
    const logPayload = { ...detectionResult, action: resolvedAction };

    await this.apilogs(
      this._appenum.EntityOfLogLevel.Info,
      `In-app browser event: ${resolvedAction} — ${detectionResult.mailApp} (${detectionResult.platform})`,
      componentName,
      logPayload,
      undefined,
      'detectMailAppInApp'
    );
  }
  async apilogs(severity, logTextMsg, componentOrPage, logRequestResponse, apiName?: any, functionName?: string) {
    try {
      this.deviceInfo = this.deviceService.getDeviceInfo();
      const ua = navigator.userAgent;
      if (this.deviceInfo.os === 'iOS' || /iPhone|iPad|iPod/.test(ua)) {
        // iOS — UA string is reliable, no Client Hints exist here at all
        this.deviceInfo.os_version = this._appservice.parseIOSVersion(ua);
        this.deviceInfo.model = this._appservice.parseIOSModel(ua);
      } else if ('userAgentData' in navigator) {
        // Android Chromium browsers — use Client Hints for real data
        try {
          const uaData = (navigator as any).userAgentData;
          const high = await uaData.getHighEntropyValues(['platformVersion', 'model']);
          this.deviceInfo.os_version = high.platformVersion || 'unknown';
          this.deviceInfo.model = high.model || 'unknown';
        } catch (e) {
          console.warn('Client Hints failed', e);
          this.deviceInfo.os_version = this._appservice.parseAndroidVersion(ua);
          this.deviceInfo.model = this._appservice.parseAndroidModel(ua);
        }
      } else {
        // Android Firefox, or any non-Chromium browser
        this.deviceInfo.os_version = this._appservice.parseAndroidVersion(ua);
        this.deviceInfo.model = this._appservice.parseAndroidModel(ua);
      }
    } catch (error) {
      console.warn('apilogs: device info detection failed, continuing without it', error);
      this.deviceInfo = this.deviceInfo || {};
    }
    if (this.shouldLog(severity)) {
      try {
        const agentId = this.encryptionService.decrypt(this.encryptionService.localstorageDecrypt(this._appenum.EntityOfLocalStorageKeys.agentId)) || null;
        const sessionId = this.encryptionService.decrypt(this.encryptionService.localstorageDecrypt(this._appenum.EntityOfLocalStorageKeys.sessionID)) || null;
        const deviceId = this.encryptionService.decrypt(this.encryptionService.localstorageDecrypt(this._appenum.EntityOfLocalStorageKeys.deviceId)) || null;
        const deviceInfo = this.encryptionService.decrypt(this.encryptionService.localstorageDecrypt(this._appenum.EntityOfLocalStorageKeys.DeviceInfo)) ? this.encryptionService.decrypt(this.encryptionService.localstorageDecrypt(this._appenum.EntityOfLocalStorageKeys.DeviceInfo)) : JSON.stringify(this.deviceInfo)
        const postJson = {
          severity: this._appenum.EntityOfLogLevel[severity], //"info/error/warning/critical/",
          logTextMsg: logTextMsg,
          logtime: new Date().toISOString(),
          userId: agentId,
          sessionId: sessionId,
          deviceId: deviceId,
          deviceInfo: deviceInfo,
          componentOrPage: componentOrPage,
          operationMode: 'Self PWA',
          logRequestResponseError: logRequestResponse,
          taskId: this.encryptionService.decrypt(this.encryptionService.localstorageDecrypt(this._appenum.EntityOfLocalStorageKeys.taskId)),
          ...(apiName ? { apiName } : {}),
          ...(functionName ? { functionName } : {}),
        };
        const res = { data: [postJson] };
        await this._appservice.postDataWithPromiseWithoutAuth(environment.endpoint.directLogging, res);
      } catch (error) {
        console.error('Error posting log data:', error);
      }
    }
  }

  async handleCustomCameraLogs(logspayload) {
    await this.apilogs(
      this._appenum.EntityOfLogLevel.Info,
      'Custom Camera Logs',
      'Custom-Camera',
      logspayload
    );
  }

  private shouldLog(level: any): boolean {
    let ret: boolean = false;
    if ((level >= this.level && level !== this._appenum.EntityOfLogLevel.Off) || this.level === this._appenum.EntityOfLogLevel.All) {
      ret = true;
    }
    return ret;
  }
}
